import { ArrowUpRight, PlayCircle } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { useAuth } from "@/auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { CHECKOUT_URL } from "@/pages/Pricing";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/191a8ee3-3432-4ba7-9a47-86bd3d7ed9e3/images/837c6b687f30fdfa20509df4174000926400964ffeea655748e4ed51305b46a3.png";

// Shared Stripe checkout link (see /app/frontend/src/pages/Pricing.jsx).
// Update the URL there — Hero imports from Pricing so both stay in sync.

export default function Hero() {
  const textRef = useReveal();
  const imageRef = useReveal({ threshold: 0.1 });
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleVideo = (e) => {
    e.preventDefault();
    const target = document.getElementById("how-it-works");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePrimary = () => {
    if (user) {
      navigate("/dashboard");
      return;
    }
    window.open(CHECKOUT_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
    >
      <div className="gp-container">
        <div className="grid grid-cols-12 gap-y-12 md:gap-x-10 lg:gap-x-16">
          {/* Text column */}
          <div ref={textRef} className="reveal col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-8 bg-[#D67229]" aria-hidden />
              <span className="gp-overline" data-testid="hero-overline">
                GushPay · Automated Oil &amp; Gas Financials
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem] font-bold tracking-tight text-[#111418] leading-[1.02]"
            >
              Automated Financials for{" "}
              <span className="italic font-normal text-[#0F4C5C]">Oil &amp; Gas.</span>
              <br className="hidden sm:block" />
              Built for{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Modern Operators.</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-2 md:h-3 bg-[#E88B46]/45 -z-0"
                />
              </span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-xl text-lg text-[#2C333A] leading-relaxed"
            >
              Stop chasing spreadsheets. Sign up, enter your wells, and
              GushPay handles Joint Interest Billing (JIB), revenue
              distribution, and your industry bookkeeping — automatically.
              <span className="block mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[#0F4C5C]">
                · No demos. No sales calls. Live in under 60 seconds.
              </span>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={handlePrimary}
                data-testid="hero-cta-button"
                className="gp-btn gp-btn-primary"
              >
                {user ? "Open Dashboard" : "Start Your Free Trial"}
                <ArrowUpRight size={16} className="-mr-1" />
              </button>
              <button
                type="button"
                onClick={handleVideo}
                data-testid="hero-video-button"
                className="gp-btn gp-btn-ghost"
              >
                <PlayCircle size={16} />
                How it works
              </button>
            </div>

            {!user && (
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#2C333A]">
                · Existing operator?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  data-testid="hero-signin-link"
                  className="underline underline-offset-4 text-[#D67229] hover:text-[#0F4C5C] transition-colors"
                >
                  Sign in to your workspace
                </button>
              </p>
            )}

            {/* Metric strip */}
            <dl className="mt-14 grid grid-cols-3 gap-6 max-w-xl border-t border-[#D3D9DE] pt-8">
              {[
                { k: "Auto", v: "JIB statements" },
                { k: "Secure", v: "ACH payouts" },
                { k: "Zero", v: "spreadsheet math" },
              ].map((item) => (
                <div key={item.k} data-testid={`hero-stat-${item.k.toLowerCase()}`}>
                  <dt className="font-display text-2xl md:text-3xl text-[#0F4C5C] font-semibold">
                    {item.k}
                  </dt>
                  <dd className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[#2C333A]">
                    {item.v}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Image column */}
          <div
            ref={imageRef}
            className="reveal col-span-12 lg:col-span-5 relative lg:pl-4"
          >
            <div className="relative">
              <div className="absolute -inset-3 border border-[#0F4C5C]/25" aria-hidden />
              <div className="relative bg-[#082933] overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="GushPay production accounting interface preview"
                  data-testid="hero-image"
                  className="block w-full h-auto object-cover aspect-[4/5] lg:aspect-[3/4]"
                  loading="eager"
                />
                <div className="gp-grain" aria-hidden />
              </div>

              <div
                className="hidden sm:flex absolute -bottom-8 -left-6 lg:-left-10 bg-[#F4F3EE] border border-[#111418] px-5 py-4 max-w-[260px] shadow-[8px_8px_0_0_#0F4C5C]"
                data-testid="hero-callout-card"
              >
                <div>
                  <p className="gp-overline">Well · Permian Basin</p>
                  <p className="mt-2 font-display text-xl text-[#111418] leading-tight">
                    +$42.8k <span className="text-[#0F4C5C]">/ mo</span>
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#2C333A] mt-1">
                    Net revenue · ORRI ready
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
